import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

function DailyWiseDateRangeDropdown({ ranges, selectedIndex, onChange, theme }) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!ranges || ranges.length <= 1) {
    return null;
  }

  const safeIndex = Math.min(Math.max(selectedIndex, 0), ranges.length - 1);
  const selectedRange = ranges[safeIndex];

  return (
    <div ref={containerRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
          theme === 'light'
            ? 'bg-white border-[#D1E2F0] text-[#24527A] hover:bg-[#F5F8FB]'
            : 'bg-[#1E2A3A] border-[#25384F] text-[#C8C8C8] hover:bg-[#243447]'
        }`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="whitespace-nowrap">{selectedRange?.label}</span>
        <svg
          className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <div
          className={`absolute right-0 top-full mt-1 z-30 min-w-[180px] max-h-[200px] overflow-y-auto rounded-md border shadow-lg ${
            theme === 'light'
              ? 'bg-white border-[#D1E2F0]'
              : 'bg-[#182433] border-[#25384F]'
          }`}
          role="listbox"
        >
          {ranges.map((range, index) => {
            const isSelected = index === safeIndex;
            return (
              <button
                key={range.id}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => {
                  onChange(index);
                  setIsOpen(false);
                }}
                className={`w-full text-left px-3 py-2 text-xs whitespace-nowrap transition-colors ${
                  isSelected
                    ? theme === 'light'
                      ? 'bg-[#24527A] text-white'
                      : 'bg-[#066FD1] text-white'
                    : theme === 'light'
                      ? 'text-[#24527A] hover:bg-[#F5F8FB]'
                      : 'text-[#C8C8C8] hover:bg-[#243447]'
                }`}
              >
                {range.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

DailyWiseDateRangeDropdown.propTypes = {
  ranges: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  selectedIndex: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  theme: PropTypes.string.isRequired,
};

export default DailyWiseDateRangeDropdown;
